import { useLocation, useNavigate } from 'react-router'
import React, { useEffect, useState } from 'react'
import { SecondaryTabBar, BrandLoader } from '@rr/ui'
import TopNav from '@/components/layout/TopNav'
import { HrefLink } from '@/src/href-link'
import { findActiveGroup, findActiveTabHref } from '@/lib/nav'
import { getAuthToken, getUser, setUser as cacheUser, clearAuth } from '@/lib/auth/session'
import { getCurrentUser } from '@/lib/api/auth'

export default function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = useLocation().pathname
  const navigate = useNavigate()

  // Hide top navigation on auth pages like login.
  const hideTopNav = pathname.startsWith('/login')

  // Auth guard for everything else: unauthenticated users land on /login.
  // We don't trust localStorage alone — verify the token against /auth/me
  // once on mount so a stale token doesn't let users see 401-spammed pages.
  //
  // Important: this MUST start as `hideTopNav` (no localStorage reads) so the
  // SSR pass and the first client render agree. Reading getUser() in the
  // initializer makes the server return false (no window) while the client
  // returns true (cached user) → hydration mismatch.
  const [authResolved, setAuthResolved] = useState(hideTopNav)

  useEffect(() => {
    if (hideTopNav) return
    let cancelled = false

    const token = getAuthToken()
    if (!token) {
      navigate('/login', { replace: true })
      return
    }
    if (getUser()) {
      // Cached user is enough — don't block render. We still hit /auth/me
      // in the background to evict a stale session if it's been revoked.
      setAuthResolved(true)
      ;(async () => {
        try {
          await getCurrentUser()
        } catch {
          if (cancelled) return
          clearAuth()
          navigate('/login', { replace: true })
        }
      })()
      return
    }
    // Token but no cached user (e.g. fresh session, page reload after login).
    ;(async () => {
      try {
        const res: any = await getCurrentUser()
        const me = res?.data ?? res
        if (cancelled) return
        if (me?.id || me?.email) {
          cacheUser(me)
          setAuthResolved(true)
        } else {
          clearAuth()
          navigate('/login', { replace: true })
        }
      } catch {
        if (cancelled) return
        clearAuth()
        navigate('/login', { replace: true })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [hideTopNav, navigate])

  if (hideTopNav) {
    return (
      <div className="min-h-screen bg-background text-foreground">{children}</div>
    )
  }

  if (!authResolved) {
    return <BrandLoader fullscreen label="Admin" />
  }

  const group = findActiveGroup(pathname)
  const activeHref = findActiveTabHref(group, pathname)

  const tabs = group.tabs.map((t) => ({
    ...t,
    active: activeHref === t.href,
  }))

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <SecondaryTabBar tabs={tabs} as={HrefLink} className="sticky top-[60px] z-30" />
      {/* Single source of page padding for every admin route — mirrors
          tenant-web, where the shell owns the page rhythm so individual
          pages never set their own outer padding / max-width. */}
      <main className="mx-auto w-full max-w-7xl px-4 py-4 sm:px-6 sm:py-6 lg:py-8">
        {children}
      </main>
    </div>
  )
}
